import React from 'react'
import { AnglePlanBadge, Logo, Modal } from 'react-angle-dashboard-components'
import { ISelectablePlan } from '../interfaces/plan'
import styled from 'styled-components'

const PriceLabel = styled.span`
  font-size: 18px;
  font-weight: 700;
  font-family: sf-pro;
`

const Title = styled.h3`
  font-family: sf-pro;
  font-size: 18px;
  font-weight: 700;
`

const SubTitle = styled.h4`
  font-family: sf-pro;
  font-size: 16px;
  font-weight: 600;
`

const DetailLabel = styled.span`
  font-size: 14px;
  font-family: sf-pro;
`

const DataRow = styled.div`
  display: flex;
  justify-content: space-between;
`

interface BenefitsModalProps {
  show: boolean
  onHide: Function
  plan: ISelectablePlan
}

export const BenefitsModal: React.FC<BenefitsModalProps> = ({
  show,
  onHide,
  plan
}) => {
  const computedDoctorCopay =
    plan.doctorVisitCopayType === 'copay'
      ? `$${plan.doctorVisitCopay}`
      : `${plan.doctorVisitCoinsurance}%`

  const computedSpecialistCopay =
    plan.specialistCopayType === 'copay'
      ? `$${plan.specialistCopay}`
      : `${plan.specialistCoinsurance}%`

  const computedErCopay =
    plan.urgentCareCopayType === 'copay'
      ? `$${plan.urgentCareCopay}`
      : `${plan.urgentCareCoinsurance}%`
  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header style={{ border: 'none' }} className="p-3">
        <div className="d-flex align-items-baseline">
          <AnglePlanBadge
            metalTier={plan.tier}
            className="mr-3"
            style={{ height: 35, width: 35 }}
            logoSize="sm"
          />
          <h2 className="fs-22">{plan.name}</h2>
        </div>
        <Logo variant="full" color="primary" size="lg" />
      </Modal.Header>
      <Modal.Body
        className="p-3"
        style={{ borderBottom: '10px solid #6D39D9' }}
      >
        <Title>Avg. Monthly cost per employee</Title>
        <hr />
        <DataRow>
          <PriceLabel>${plan.employerCostPerEmployee} /mo</PriceLabel>
          <DetailLabel>Employer Cost</DetailLabel>
        </DataRow>
        <DataRow>
          <PriceLabel>${plan.employeeCostPerEmployee} /mo</PriceLabel>
          <DetailLabel>Employee Cost</DetailLabel>
        </DataRow>
        <DataRow className="mb-3">
          <PriceLabel>${plan.totalCostPerEmployee} /mo</PriceLabel>
          <DetailLabel>Total Cost</DetailLabel>
        </DataRow>

        <SubTitle>Other Costs (In-network)</SubTitle>
        <hr />
        <DataRow>
          <PriceLabel>
            ${plan.deductible} / ${plan.deductibleFamily}
          </PriceLabel>
          <DetailLabel>Deductible (Individual/Family)</DetailLabel>
        </DataRow>
        <DataRow className="mb-5">
          <PriceLabel>
            ${plan.maxOOP} / ${plan.maxOOPFamily}
          </PriceLabel>
          <DetailLabel>Out of Pocket Max (Individual/Family)</DetailLabel>
        </DataRow>
        <DataRow>
          <PriceLabel>{computedDoctorCopay}</PriceLabel>
          <DetailLabel>Primary Care Visit Copay</DetailLabel>
        </DataRow>
        <DataRow>
          <PriceLabel>{computedSpecialistCopay}</PriceLabel>
          <DetailLabel>Specialist Copay</DetailLabel>
        </DataRow>
        <DataRow>
          <PriceLabel>{computedErCopay}</PriceLabel>
          <DetailLabel>ER Copay</DetailLabel>
        </DataRow>
        <DataRow className="mb-3">
          <PriceLabel>${plan.genericDrug}</PriceLabel>
          <DetailLabel>Generic Drug</DetailLabel>
        </DataRow>
        <small className="text-secondary">
          Health insurance plan underwritten by Angle Insurance Company of Utah
        </small>
      </Modal.Body>
    </Modal>
  )
}
